import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";

export type ContributionStatus = "RECEIVED" | "CANCELLED";

export type FinanceContribution = {
  id: string;
  code: string;
  receivedDate: string;
  companyId: string;
  company: { id: string; code: string; name: string; currency: string };
  partnerId: string;
  partner: { id: string; name: string; shortCode: string };
  accountId: string;
  account: { id: string; code: string; name: string; currency: string };
  amount: string;
  currency: string;
  creditedAmount: string;
  creditedCurrency: string;
  rate: string | null;
  reference: string | null;
  notes: string | null;
  sourceType: string | null;
  status: ContributionStatus;
  cancelReason: string | null;
  /** Months chosen, as "YYYY-MM"; the rest counts in the month received. */
  allocations: { period: string; amount: string }[];
  unallocatedAmount: string;
};

export type ContributionPartner = { id: string; code: string; name: string; shortCode: string; isActive: boolean };

export type ContributionOptions = {
  companies: {
    id: string;
    code: string;
    name: string;
    currency: string;
    partners: ContributionPartner[];
    accounts: { id: string; code: string; name: string; currency: string; type: string }[];
  }[];
};

export type ContributionSummary = {
  company: { id: string; code: string; name: string; currency: string };
  year: number;
  months: string[];
  rows: {
    partner: ContributionPartner;
    contributed: string;
    yearTotal: string;
    months: { month: string; amount: string }[];
  }[];
  monthTotals: { month: string; amount: string }[];
};

const KEY = ["finance-contributions"] as const;

export function useContributionList(filters: {
  companyId?: string;
  status?: ContributionStatus | "";
}): UseQueryResult<FinanceContribution[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "list", filters, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ contributions: FinanceContribution[] }>("/finance-contributions", {
        token: accessToken,
        query: { companyId: filters.companyId || undefined, status: filters.status || undefined },
      });
      return res.contributions;
    },
  });
}

export function useContributionOptions(): UseQueryResult<ContributionOptions> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "options", accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () => apiFetch<ContributionOptions>("/finance-contributions/options", { token: accessToken }),
  });
}

export function useContributionSummary(companyId: string, year: number): UseQueryResult<ContributionSummary> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...KEY, "summary", companyId, year, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!companyId,
    queryFn: () =>
      apiFetch<ContributionSummary>("/finance-contributions/summary", {
        token: accessToken,
        query: { companyId, year: String(year) },
      }),
  });
}

export function useContributionAction() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ method, path, body }: { method: "POST" | "PUT"; path: string; body: unknown }) =>
      apiFetch(`/finance-contributions${path}`, { method, token: accessToken, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
      // Contributions move account balances.
      queryClient.invalidateQueries({ queryKey: ["finance-accounts"] });
    },
  });
}
